import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { fetchHub } from "@/lib/api";
import { useSettings, keyForPath } from "@/context/SettingsContext";
import { useLang } from "@/context/LanguageContext";
import { PageHero } from "@/components/PageHero";
import RelatedServices from "@/components/RelatedServices";
import { heroForSection } from "@/lib/heroes";
import { stripHtml } from "@/lib/html";
import { ArrowRight, Landmark } from "lucide-react";

function HubCard({ e }) {
  const { pick } = useLang();
  return (
    <Link to={`/learn/${e.slug}`} data-testid={`hub-card-${e.slug}`} className="group flex flex-col border border-line bg-[var(--surface)] p-7 transition-colors hover:border-[var(--gold)]">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-deva text-2xl text-ink deva-lh">{e.deva_name}</h3>
        <ArrowRight className="h-4 w-4 shrink-0 text-ink-3 transition-transform group-hover:translate-x-1 group-hover:text-saffron" />
      </div>
      <p className="mt-1 font-serif-latin text-lg text-ink">{pick(e.title_hi, e.title)}</p>
      <p className="mt-1 text-sm text-gold">{pick(e.subtitle_hi, e.subtitle)}</p>
      <p className="mt-4 text-[15px] leading-relaxed text-ink-2 line-clamp-3">{stripHtml(pick(e.summary_hi, e.summary))}</p>
    </Link>
  );
}

export default function HubSection({ section }) {
  const [data, setData] = useState(null);
  const { pathname } = useLocation();
  const { label, heading } = useSettings();
  const { t, lang, pick } = useLang();
  useEffect(() => { fetchHub(section).then(setData).catch(() => {}); }, [section]);
  const meta = data?.meta;
  const items = data?.items || [];
  const groupOrder = data?.group_order || [];
  const groupLabels = data?.group_labels || {};

  const sec = meta ? label(keyForPath(pathname), meta.deva, meta.title) : null;
  const hd = heading(keyForPath(pathname), { title_deva: sec?.deva || meta?.deva, title_en: sec?.en || meta?.title, sub_deva: meta?.tagline, sub_en: meta?.tagline });
  const countWord = (n) => (n === 1 ? t("text_one") : t("text_many"));

  const grouped = groupOrder.length > 0;
  const byGroup = {};
  if (grouped) items.forEach((e) => { (byGroup[e.group || "More"] ||= []).push(e); });
  const orderedGroups = grouped
    ? [...groupOrder, "More"].filter((g) => byGroup[g]?.length)
    : [];

  return (
    <div data-testid={`hub-${section}`}>
      <PageHero
        art={heroForSection(section)}
        overline={hd.title_en || (sec ? sec.en : meta?.title)}
        deva={hd.title_deva || (sec ? sec.deva : meta?.deva)}
        tagline={(lang === "hi" ? hd.sub_deva : hd.sub_en) || meta?.tagline}
      >
        {grouped && orderedGroups.length > 1 && (
          <div className="mt-8 flex flex-wrap gap-2">
            {orderedGroups.map((g) => (
              <a key={g} href={`#${g.replace(/[^a-z]/gi, "-").toLowerCase()}`} className="border border-line bg-surface/70 px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
                {groupLabels[g] || g} <span className="text-ink-3">{byGroup[g].length}</span>
              </a>
            ))}
          </div>
        )}
      </PageHero>

      <section className="mx-auto max-w-5xl px-5 py-14 md:px-8 md:py-20">
        {grouped ? (
          <div className="space-y-16">
            {orderedGroups.map((g) => (
              <div key={g} id={g.replace(/[^a-z]/gi, "-").toLowerCase()} className="scroll-mt-24">
                <div className="mb-6 flex items-baseline gap-3 border-b border-line pb-3">
                  <h2 className="font-serif-latin text-2xl text-ink">{groupLabels[g] || g}</h2>
                </div>
                <div className="grid gap-6 md:grid-cols-2">
                  {byGroup[g].map((e) => <HubCard key={e.slug} e={e} />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {items.map((e) => <HubCard key={e.slug} e={e} />)}
          </div>
        )}

        {section === "pilgrimage" && (
          <Link to="/temples" data-testid="pilgrimage-temples-link"
            className="group mt-14 flex items-center gap-4 overflow-hidden rounded-xl border border-line bg-[var(--surface)] p-5 transition-all duration-300 hover:border-[var(--gold)] hover:shadow-lg md:p-6">
            <Landmark className="h-8 w-8 shrink-0 text-saffron" />
            <div className="min-w-0 flex-1">
              <p className="font-serif-latin text-lg text-ink">{pick("भारत के मंदिर", "Temples of Bharat")}</p>
              <p className="mt-0.5 text-sm text-ink-2">{pick("ज्योतिर्लिंग, शक्तिपीठ, अष्टविनायक एवं अन्य मंदिर-परिक्रमाएँ।", "Jyotirlingas, Shakti Peethas, Ashta Vinayaka and the great temple circuits.")}</p>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0 text-ink-3 transition-transform group-hover:translate-x-1 group-hover:text-saffron" />
          </Link>
        )}

        <RelatedServices section={section} />
      </section>
    </div>
  );
}
